import "./App.css";
import {
  Alert,
  Button,
  Card,
  Progress,
  Segmented,
  Spin,
  Statistic,
  Tag,
  Row,
  Col,
  FloatButton
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PoweroffOutlined,
  CaretRightOutlined,
  PauseOutlined,
  VideoCameraOutlined
} from "@ant-design/icons";
import { React, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, onValue, ref, set, update } from "firebase/database";
import { auth, database as db } from "./firebase";
import { PickerView } from "antd-mobile";

const { Countdown } = Statistic;

const timePickerColumns = [
  [],
  [
    { label: "00", value: 0 },
    { label: "15", value: 15 },
    { label: "30", value: 30 },
    { label: "45", value: 45 },
  ],
  [
    { label: "AM", value: "am" },
    { label: "PM", value: "pm" },
  ],
];
function populateHourPicker() {
  for (let i = 1; i < 13; i++)
    timePickerColumns[0].push({ label: i.toString(), value: i });
}
populateHourPicker();

function isOnline(updatedAt) {
  return Date.now() - updatedAt < 120 * 1000;
}

function statusIcon(online) {
  return online ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
}

function SwitchUI() {
  const navigate = useNavigate();

  const [sensorStatus, setSensorStatus] = useState({
    icon: <InfoCircleOutlined />,
    connected: false,
    loading: true,
    reading: 0,
    rate: 0,
  });
  const [controlStatus, setControlStatus] = useState({
    icon: <InfoCircleOutlined />,
    connected: false,
    loading: true,
    state: false,
    timer: 0,
    disabled: false,
    offline: false,
  });
  const [controllerSched, setControllerSched] = useState({
    hour: 0,
    min: 0,
    am: true,
  });
  const [schedLoading, setSchedLoading] = useState(false);

  const [serverStatus, setServerStatus] = useState(
    "Connecting to the control server"
  );

  const [sensorPanel, setSensorPanel] = useState("level");
  const [controlPanel, setControlPanel] = useState("state");

  useEffect(() => {
    if (!auth.currentUser) navigate("/login");

    const connectionRef = ref(db, ".info/connected");
    onValue(connectionRef, (snap) => {
      if (snap.val()) setServerStatus("Server online");
      else setServerStatus("Disconnected...");
    });

    const sensorRef = ref(db, "/waterLvl");
    get(sensorRef).then((snapshot) => {
      if (snapshot.exists()) {
        const sensorData = snapshot.val();
        if (!sensorData) return;

        const online = isOnline(sensorData.updatedAt);
        setSensorStatus({
          reading: sensorData.lvl,
          rate: sensorData.rate,
          loading: false,
          connected: online,
          icon: statusIcon(online),
        });
      }
    });

    const controlRef = ref(db, "/waterPumpStatus");
    get(controlRef).then((snapshot) => {
      if (snapshot.exists()) {
        const controlData = snapshot.val();

        const online = isOnline(controlData.updatedAt);
        setControlStatus({
          state: controlData.state,
          timer: controlData.targetTime * 1000,
          connected: online,
          loading: false,
          icon: statusIcon(online),
          disabled: controlData.disabled,
          offline: controlData.offline,
        });
      }
    });

    const controllerSchedRef = ref(db, "/waterPumpStatus/schedule");
    get(controllerSchedRef).then((snapshot) => {
      if (snapshot.exists()) {
        const schedule = snapshot.val();

        setControllerSched({
          hour: schedule.hour,
          min: schedule.min,
          am: schedule.am,
        });
      }
    });

    onValue(sensorRef, (snapshot) => {
      const sensorData = snapshot.val();
      if (!sensorData) return;

      const online = isOnline(sensorData.updatedAt);
      setSensorStatus({
        reading: sensorData.lvl,
        rate: sensorData.rate,
        loading: false,
        connected: online,
        icon: statusIcon(online),
      });
    });

    onValue(controlRef, (snapshot) => {
      const controlData = snapshot.val();
      if (!controlData) return;

      const online = isOnline(controlData.updatedAt);
      setControlStatus({
        state: controlData.state,
        timer: controlData.targetTime * 1000,
        connected: online,
        loading: false,
        icon: statusIcon(online),
        disabled: controlData.disabled,
        offline: controlData.offline,
      });

      setControllerSched({
        hour: controlData.schedule.hour,
        min: controlData.schedule.min,
        am: controlData.schedule.am,
      });
    });
  }, [navigate]);

  const toggleControl = () => {
    setControlStatus({ ...controlStatus, loading: true });

    const controlRef = ref(db, "/setControl/status");
    const randNum = Math.floor(Math.random() * 1000);
    set(controlRef, !controlStatus.state ? randNum : -1 * randNum);
  };

  const toggleDisabled = () => {
    setControlStatus({ ...controlStatus, loading: true });

    const controlRef = ref(db, "/setControl");
    const randNum = Math.floor(Math.random() * 1000);
    update(controlRef, {
      status: controlStatus.state ? randNum : -1 * randNum, 
      disabled: !controlStatus.disabled ? randNum : -1 * randNum
    });
  };

  const changeSchedule = (value) => {
    if (value == null) return;

    setSchedLoading(true);

    const hour = value[0];
    const min = value[1];
    const am = value[2] ? true : false;

    const sched = { hour: hour, min: min, am: am };
    setControllerSched(sched);

    const controllerSchedRef = ref(db, "/setControl/schedule");
    update(controllerSchedRef, sched).then(() => {
      setSchedLoading(false);
    });
  };

  const levelIndicator = () => {
    return (
      <Progress
        type="circle"
        className="level-indicator"
        size={150}
        percent={parseInt(sensorStatus.reading)}
        status={
          sensorStatus.connected && !sensorStatus.loading
            ? "normal"
            : "exception"
        }
      />
    );
  };

  const rateIndicator = () => {
    return (
      <Statistic
        value={sensorStatus.rate}
        className="rate-indicator"
        valueStyle={{
          color: sensorStatus.rate >= 0 ? "#3f8600" : "#cf1322",
        }}
        prefix={
          sensorStatus.rate >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />
        }
        suffix="%/min"
      />
    );
  };

  const stateIndicator = () => {
    var icon = <PauseOutlined/>;
    var btnTxt = "Pause";
    if(controlStatus.disabled) {
      icon = <CaretRightOutlined />;
      btnTxt = "Resume";
    } 

    var powerBtnColor = "#00e384";
    if(!controlStatus.state) powerBtnColor = "#dc4446";
    if(controlStatus.disabled) powerBtnColor = "#808080";

    return (
      <Spin spinning={controlStatus.loading}>
        <Row gutter={[16, {xs: 8, sm: 16}]}>
            <Col span={24} >
            <PoweroffOutlined
              className="powerBtn"
              style={{ color: powerBtnColor }}
              onClick={toggleControl}
              />
            </Col>
            <Col span={6} offset={16}>
              <Button type="primary" icon={icon} onClick={toggleDisabled}>
                {btnTxt}
              </Button>
            </Col>
        </Row>
      </Spin>
    );
  };

  const timerPanel = () => {
    console.log(controlStatus.timer)
    return controlStatus.disabled ? (
      <Countdown
        className="timer"
        valueStyle={{ color: "gray" }}
        value={0}
        format="mm:ss"
      />
    ) : (
      <Countdown className="timer" value={controlStatus.timer} format="mm:ss" />
    );
  };

  const schedulePanel = () => {
    const hr = controllerSched.hour;
    const min = controllerSched.min;
    const amVal = controllerSched.am ? "am" : "pm";

    return controlStatus.disabled ? (
      "Unavailable, please resume the pump operations and try again"
    ) : (
      <PickerView
        aria-disabled
        className="schedule"
        columns={timePickerColumns}
        mouseWheel={true}
        defaultValue={[hr, min, amVal]}
        onChange={changeSchedule}
        style={{ "--height": "100%" }}
        loading={schedLoading}
      />
    );
  };

  const cardTitle = (title, sensorStatus) => {
    let color = "processing";
    let statusMsg = "connecting...";
    if (!sensorStatus.loading) {
      color = "success";
      if ("offline" in sensorStatus && sensorStatus.offline) color = "warning";
      else if (!sensorStatus.connected) color = "error";

      statusMsg = "online";
      if ("offline" in sensorStatus && sensorStatus.offline)
        statusMsg = "offline";
      else if (!sensorStatus.connected) 
        statusMsg = "disconnected";
    }
    return (
      <div className="card-title">
        <span>{title}</span>
        <Tag icon={sensorStatus.icon} color={color} className="status">
          {statusMsg}
        </Tag>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <FloatButton icon={<VideoCameraOutlined />} onClick={() => {navigate("/sprinkler")}}></FloatButton>

      <Alert
        type="info"
        message={serverStatus}
        showIcon={true}
        style={{ width: "100%" }}
      />
      <div className="panel">
        <Card
          title={cardTitle("Water", sensorStatus)}
          bordered={false}
          className="sensor-card"
        >
          <Segmented
            options={["level", "rate"]}
            block
            onChange={(val) => setSensorPanel(val)}
          />
          {sensorPanel == "level" ? levelIndicator() : rateIndicator()}
        </Card>
        <Card
          title={cardTitle("Pump", controlStatus)}
          bordered={false}
          className="control-card"
        >
          <Segmented
            options={["state", "timer", "schedule"]}
            block
            onChange={(val) => setControlPanel(val)}
          />
          {controlPanel == "state"
            ? stateIndicator()
            : controlPanel == "timer"
            ? timerPanel()
            : schedulePanel()}
        </Card>
      </div>
    </div>
  );
}

export default SwitchUI;
